import {
    escapeHTML,
    formatEarthquakeDateTime,
    getEarthquakeIntensityColor,
    getEarthquakeIntensityLabel,
    getEarthquakeIntensityRank
} from '../helpers/helpers-earthquake';
import Panel from './panel';

const SELECT_ALL = '__all__';

function buildHeaderHtml() {
    return [
        '<div class="earthquake-panel-header">',
        '<span class="earthquake-panel-title-text">地震情報</span>',
        '<span class="earthquake-panel-caption">過去30日間の地震の情報が表示できます</span>',
        '</div>'
    ].join('');
}

function buildSummaryRow(label, value, className = '') {
    return [
        `<div class="earthquake-summary-row ${className}">`,
        `<div class="earthquake-summary-label">${escapeHTML(label)}</div>`,
        `<div class="earthquake-summary-value">${value || '-'}</div>`,
        '</div>'
    ].join('');
}

function buildMunicipalityRows(municipalities) {
    if (!municipalities.length) {
        return '<div class="earthquake-empty">表示できる市町村データがありません。</div>';
    }

    return municipalities.map(municipality => {
        const intensityLabel = getEarthquakeIntensityLabel(municipality.rawIntensity),
            mapStatus = municipality.hasCoordinate ? '' : '<span class="earthquake-municipality-note">地図表示なし</span>';

        return [
            '<div class="earthquake-municipality-row">',
            `<div class="earthquake-intensity-badge" style="background-color: ${getEarthquakeIntensityColor(municipality.rawIntensity)};">${escapeHTML(intensityLabel || '-')}</div>`,
            '<div class="earthquake-municipality-text">',
            `<div class="earthquake-municipality-name">${escapeHTML(municipality.prefname)} ${escapeHTML(municipality.cityname || municipality.areaname)}</div>`,
            `<div class="earthquake-municipality-area">${escapeHTML(municipality.areaname || '')}${mapStatus}</div>`,
            '</div>',
            '</div>'
        ].join('');
    }).join('');
}

function buildEarthquakeEventRows(earthquakes) {
    if (!earthquakes.length) {
        return '<div class="earthquake-empty">地震情報はありません。</div>';
    }

    return earthquakes.map(earthquake => {
        const originTime = formatEarthquakeDateTime(
                earthquake.origintime ||
                earthquake.occurrence_datetime ||
                earthquake.report_datetime
            ),
            intensityColor = getEarthquakeIntensityColor(earthquake.maxint),
            intensityLabel = earthquake.maxIntensityLabel || getEarthquakeIntensityLabel(earthquake.maxint);

        return [
            `<div class="earthquake-event-row" data-eventid="${escapeHTML(earthquake.eventid)}" role="button" tabindex="0">`,
            `<div class="earthquake-intensity-badge" style="background-color: ${intensityColor};">${escapeHTML(intensityLabel || '-')}</div>`,
            '<div class="earthquake-municipality-text">',
            `<div class="earthquake-municipality-name">${escapeHTML(earthquake.hypocentername || '震源地不明')}</div>`,
            `<div class="earthquake-municipality-area">${escapeHTML(originTime || '時刻不明')} / M${escapeHTML(earthquake.magnitude || '-')}</div>`,
            '</div>',
            '</div>'
        ].join('');
    }).join('');
}

function buildAllEarthquakePanelHtml(earthquakes) {
    const latestEarthquake = earthquakes[0] || null,
        strongestEarthquake = earthquakes.reduce((bestEarthquake, earthquake) => {
            if (!bestEarthquake) {
                return earthquake;
            }
            return getEarthquakeIntensityRank(earthquake.maxint) > getEarthquakeIntensityRank(bestEarthquake.maxint) ?
                earthquake :
                bestEarthquake;
        }, null),
        latestTime = latestEarthquake ?
            formatEarthquakeDateTime(
                latestEarthquake.origintime ||
                latestEarthquake.occurrence_datetime ||
                latestEarthquake.report_datetime
            ) :
            '',
        tsunamiRisk = earthquakes.some(earthquake => {
            const value = String(earthquake.tsunamiRisk || '').trim();

            return value && value !== '情報なし';
        }) ? 'あり' : '情報なし',
        maxIntensityLabel = strongestEarthquake ?
            (strongestEarthquake.maxIntensityLabel || getEarthquakeIntensityLabel(strongestEarthquake.maxint)) :
            '-';

    return [
        '<div class="earthquake-panel-body">',
        '<div class="earthquake-event-switcher">',
        '<select class="earthquake-event-select" aria-label="地震情報の選択"></select>',
        '</div>',
        '<div class="earthquake-summary-grid">',
        buildSummaryRow('件数', escapeHTML(`${earthquakes.length}件`)),
        buildSummaryRow('最新発生時刻', escapeHTML(latestTime || '-')),
        buildSummaryRow(
            '最大震度',
            `<span class="earthquake-intensity-badge" style="background-color: ${getEarthquakeIntensityColor(strongestEarthquake?.maxint)};">${escapeHTML(maxIntensityLabel)}</span>`,
            'earthquake-summary-row-highlight'
        ),
        buildSummaryRow('津波の危険性', escapeHTML(tsunamiRisk)),
        '</div>',
        '<div class="earthquake-section-title">地震一覧</div>',
        `<div class="earthquake-municipality-list">${buildEarthquakeEventRows(earthquakes)}</div>`,
        '</div>'
    ].join('');
}

function buildEarthquakePanelHtml(earthquake) {
    const originTime = formatEarthquakeDateTime(earthquake.origintime || earthquake.report_datetime),
        tsunamiRisk = earthquake.tsunamiRisk ? escapeHTML(earthquake.tsunamiRisk) : '情報なし';

    return [
        '<div class="earthquake-panel-body">',
        '<div class="earthquake-event-switcher">',
        '<select class="earthquake-event-select" aria-label="地震情報の選択"></select>',
        '</div>',
        '<div class="earthquake-summary-grid">',
        buildSummaryRow('発生時刻', escapeHTML(originTime)),
        buildSummaryRow('震源地', escapeHTML(earthquake.hypocentername || '不明')),
        buildSummaryRow('マグニチュード', escapeHTML(earthquake.magnitude || '-')),
        buildSummaryRow(
            '最大震度',
            `<span class="earthquake-intensity-badge" style="background-color: ${getEarthquakeIntensityColor(earthquake.maxint)};">${escapeHTML(earthquake.maxIntensityLabel || '-')}</span>`,
            'earthquake-summary-row-highlight'
        ),
        '</div>',
        '<div class="earthquake-section-title">津波の危険性</div>',
        `<div class="earthquake-tsunami-risk">${tsunamiRisk}</div>`,
        '<div class="earthquake-section-title">各地域の震度</div>',
        `<div class="earthquake-municipality-list">${buildMunicipalityRows(earthquake.municipalities)}</div>`,
        earthquake.hiddenMunicipalityCount > 0 ?
            `<div class="earthquake-panel-note">座標未登録の市町村 ${earthquake.hiddenMunicipalityCount} 件は地図上に表示していません。</div>` :
            '',
        '</div>'
    ].join('');
}

function buildEarthquakeOptionLabel(earthquake) {
    const originTime = formatEarthquakeDateTime(earthquake.origintime || earthquake.occurrence_datetime || earthquake.report_datetime),
        hypocenterName = earthquake.hypocentername || '震源地不明';

    return `${originTime || '時刻不明'} ${hypocenterName}`;
}

function bindSelectableRows(container, selector, onSelect) {
    const rows = container ? Array.from(container.querySelectorAll(selector)) : [];

    rows.forEach(row => {
        const triggerSelection = () => {
            const eventId = row.dataset.eventid;

            if (eventId) {
                onSelect(eventId);
            }
        };

        row.addEventListener('click', event => {
            event.stopPropagation();
            triggerSelection();
        });
        row.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                triggerSelection();
            }
        });
    });
}

export default class extends Panel {

    constructor(options) {
        super(Object.assign({
            className: 'earthquake-panel',
            earthquakes: [],
            selectedEventId: '',
            headerClickAction: 'close'
        }, options));
    }

    addTo(map) {
        const me = this;

        me._closing = false;
        super.addTo(map)
            .setTitle(buildHeaderHtml())
            .updateContent();

        me._container.querySelector('#panel-button').addEventListener('click', event => {
            event.stopPropagation();
            me.remove();
        });

        return me;
    }

    setEarthquakes(earthquakes, selectedEventId = this._options.selectedEventId) {
        this._options.earthquakes = Array.isArray(earthquakes) ? earthquakes : [];
        this._options.selectedEventId = selectedEventId || '';

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    setSelectedEventId(selectedEventId) {
        this._options.selectedEventId = selectedEventId || '';

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    isAllSelected() {
        return this._options.selectedEventId === SELECT_ALL;
    }

    getSelectedEarthquake() {
        const earthquakes = this._options.earthquakes || [],
            selectedEventId = this._options.selectedEventId;

        if (selectedEventId === SELECT_ALL) {
            return null;
        }
        return earthquakes.find(earthquake => earthquake.eventid === selectedEventId) || earthquakes[0] || null;
    }

    updateEventOptions() {
        const me = this,
            select = me._eventSelect,
            earthquakes = me._options.earthquakes || [],
            selectedEarthquake = me.getSelectedEarthquake(),
            selectedValue = me.isAllSelected() ?
                SELECT_ALL :
                (selectedEarthquake ? selectedEarthquake.eventid : earthquakes[0]?.eventid || '');

        if (!select) {
            return me;
        }

        select.innerHTML = '';

        if (!earthquakes.length) {
            const option = document.createElement('option');

            option.value = '';
            option.textContent = '地震情報はありません';
            select.appendChild(option);
            select.disabled = true;
            return me;
        }

        const allOption = document.createElement('option');

        allOption.value = SELECT_ALL;
        allOption.textContent = 'すべて';
        select.appendChild(allOption);

        earthquakes.forEach(earthquake => {
            const option = document.createElement('option');

            option.value = earthquake.eventid;
            option.textContent = buildEarthquakeOptionLabel(earthquake);
            select.appendChild(option);
        });
        select.disabled = false;
        select.value = selectedValue;

        return me;
    }

    bindEventSelect() {
        const me = this,
            select = me._container && me._container.querySelector('.earthquake-event-select');

        if (!select) {
            me._eventSelect = null;
            return me;
        }
        if (select === me._eventSelect) {
            return me;
        }

        me._eventSelect = select;
        ['mousedown', 'click', 'touchstart'].forEach(eventName => {
            select.addEventListener(eventName, event => {
                event.stopPropagation();
            });
        });
        select.addEventListener('change', event => {
            const onSelectEarthquake = me._options.onSelectEarthquake;

            me.setSelectedEventId(event.target.value);
            if (onSelectEarthquake) {
                onSelectEarthquake(event.target.value);
            }
        });

        return me;
    }

    bindEventRows() {
        const me = this,
            onSelectEarthquake = me._options.onSelectEarthquake;

        if (!onSelectEarthquake || !me._container) {
            return me;
        }

        bindSelectableRows(me._container, '.earthquake-event-row[data-eventid]', eventId => {
            me.setSelectedEventId(eventId);
            onSelectEarthquake(eventId);
        });

        return me;
    }

    updateContent() {
        const earthquakes = this._options.earthquakes || [],
            earthquake = this.getSelectedEarthquake();

        this.setTitle(buildHeaderHtml());

        if (!earthquakes.length) {
            this.setHTML('<div class="earthquake-empty">地震情報はありません。</div>');
            this._eventSelect = null;
            return this;
        }

        this.setHTML(
            this.isAllSelected() ?
                buildAllEarthquakePanelHtml(earthquakes) :
                buildEarthquakePanelHtml(earthquake)
        );
        this.bindEventSelect();
        this.updateEventOptions();
        this.bindEventRows();
        return this;
    }

    isClosing() {
        return !!this._closing;
    }

    remove() {
        const me = this,
            onClose = me._options.onClose;

        if (!me.isOpen()) {
            return me;
        }

        me._closing = true;
        super.remove();

        if (onClose) {
            onClose();
        }
        setTimeout(() => {
            me._closing = false;
        }, 300);
        return me;
    }

}
